import DatePickerField from '@acorn-portal/shared/src/components/datePicker/DropDownDatePicker';
import AutoCompleteFormField from '@acorn-portal/shared/src/components/inputs/AutoCompleteFormField';
import FormTextField from '@acorn-portal/shared/src/components/inputs/FormTextField';
import RadioGroupButton from '@acorn-portal/shared/src/components/inputs/radio-button-group';
import SelectFormField, { IOptions } from '@acorn-portal/shared/src/components/inputs/select';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { MotoringConviction } from '@acorn-portal/shared/src/services/api';
import { DISPLAY_DATE, ISO_UTC_FORMAT, PENALTY_POINTS } from '@acorn-portal/shared/src/utils/constants';
import { MOTORING_CONVICTIONS_COMMON_ERRORS } from '@acorn-portal/shared/src/utils/constants/ErrorMessages';
import { generateDate } from '@acorn-portal/shared/src/utils/date-utils';
import { getMotoringConvictionsSchema } from '@acorn-portal/shared/src/utils/validation/MotoringConvictionsSchema';
import { Box, Button, Collapse, FormLabel, Grid } from '@mui/material';
import { format, isAfter, subYears } from 'date-fns';
import { FormikContextType, FormikHelpers, useFormik } from 'formik';
import { FC, useEffect, useState } from 'react';
import MiniTable, { IColumn, TableType } from '../../components/table/mini-table';
import { useFormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

interface IDateObject {
	day: string;
	month: string;
	year: string;
}

interface IMotoringConvictions {
	dateOfConviction: IDateObject;
	dateOfOffence: IDateObject;
	offenceCode: string;
	wasDisqualified: string;
	banStartDate: IDateObject;
	banEndDate: IDateObject;
	numberOfPoints: string;
	fineAmount: string;
	isPending: string;
}
interface IProps {
	data?: MotoringConviction[] | null;
	hasMotoringConvictions: boolean;
	onChange: (value: MotoringConviction[]) => void;
	isDateAfterDOB: (date: Date, formikHelpers: FormikHelpers<IMotoringConvictions>, field: string) => boolean;
	isAdditionalDriver?: boolean;
}
const MotoringConvictionsForm: FC<IProps> = ({
	data,
	hasMotoringConvictions,
	onChange,
	isDateAfterDOB,
	isAdditionalDriver = false,
}) => {
	const lists = useAppSelector((redux) => redux.referenceData);
	const [selectedItemIndex, setSelectedItemIndex] = useState<number>();
	const [isOpenModal, setIsOpenModal] = useState(false);
	const [motoringConvictions, setMotoringConvictions] = useState<MotoringConviction[]>(data || []);
	const [showForm, setShowForm] = useState(motoringConvictions.length === 0);

	const maxConvictionDate = subYears(new Date(), 5).getFullYear();

	const handleSubmit = (values: IMotoringConvictions, formikHelpers: FormikHelpers<IMotoringConvictions>) => {
		const today = new Date();
		let banStartDate;
		let banEndDate;

		const dateOfConviction = generateDate(values.dateOfConviction);
		if (isAfter(dateOfConviction, today)) {
			formikHelpers.setErrors({
				dateOfConviction: {
					day: "The date of conviction can't be in the future.",
				},
			});
			return;
		}
		if (!isDateAfterDOB(dateOfConviction, formikHelpers, 'dateOfConviction')) return;
		if (values.wasDisqualified === 'yes') {
			banStartDate = generateDate(values.banStartDate);
			banEndDate = generateDate(values.banEndDate);
			if (isAfter(banStartDate, today)) {
				formikHelpers.setErrors({
					banStartDate: {
						day: "The ban start date can't be in the future.",
					},
				});
				return;
			}
			if (
				!isDateAfterDOB(banStartDate, formikHelpers, 'banStartDate') ||
				!isDateAfterDOB(banEndDate, formikHelpers, 'banEndDate')
			)
				return;
			if (isAfter(dateOfConviction, banStartDate)) {
				formikHelpers.setErrors({
					banStartDate: {
						day: MOTORING_CONVICTIONS_COMMON_ERRORS.banDateBeforeDate,
					},
				});
				return;
			}

			if (isAfter(banStartDate, banEndDate)) {
				formikHelpers.setErrors({
					banEndDate: {
						day: MOTORING_CONVICTIONS_COMMON_ERRORS.endDateBeforeStart,
					},
				});
				return;
			}
		}

		const convictionDate = format(dateOfConviction, ISO_UTC_FORMAT);

		const item: MotoringConviction = {
			dateOfConviction: convictionDate,
			dateOfOffence: convictionDate,
			offenceCode: values.offenceCode,
			wasDisqualified: values.wasDisqualified === 'yes',
			banStartDate: banStartDate && format(banStartDate, ISO_UTC_FORMAT),
			banEndDate: banEndDate && format(banEndDate, ISO_UTC_FORMAT),
			numberOfPoints: Number.parseInt(values.numberOfPoints, 10),
			fineAmount: Number.parseFloat(values.fineAmount),
			isPending: values.isPending === 'yes',
		};
		if (selectedItemIndex !== undefined && Number(selectedItemIndex) >= 0) {
			const tempArray = [...motoringConvictions];
			tempArray[selectedItemIndex] = { ...item };
			setMotoringConvictions(tempArray);
			setSelectedItemIndex(undefined);
			onChange(tempArray);
			setShowForm(false);
			formikHelpers.resetForm();
			return;
		}
		const newArray = [...motoringConvictions, item];
		setMotoringConvictions(newArray);
		onChange(newArray);
		setShowForm(false);
		formikHelpers.resetForm();
	};

	const formik = useFormik({
		initialValues: {
			dateOfConviction: {
				day: '',
				month: '',
				year: '',
			},
			dateOfOffence: {
				day: '',
				month: '',
				year: '',
			},
			offenceCode: '',
			wasDisqualified: '',
			banStartDate: {
				day: '',
				month: '',
				year: '',
			},
			banEndDate: {
				day: '',
				month: '',
				year: '',
			},
			numberOfPoints: '',
			fineAmount: '',
			isPending: '',
		},
		validationSchema: getMotoringConvictionsSchema(isAdditionalDriver),
		onSubmit: handleSubmit,
	});

	useFormikLogErrors(formik as FormikContextType<unknown>);

	useEffect(() => {
		if (!hasMotoringConvictions) {
			const newArray: MotoringConviction[] = [];
			setMotoringConvictions(newArray);
			onChange(newArray);
			formik.resetForm();
		}
	}, [hasMotoringConvictions]);

	const GetValue = (x: string) => (lists.convictionCodes.find((item) => item.value === x)?.label as string) || x;

	const COLUMNS = [
		{
			title: 'Offence	',
			field: 'modificationCode',
			render: (rowData) => GetValue(rowData.offenceCode),
		},
		{
			title: 'Date of Conviction',
			field: 'value',
			render: (rowData) =>
				rowData.dateOfConviction &&
				new Date(rowData.dateOfConviction) instanceof Date &&
				!isNaN(new Date(rowData.dateOfConviction).valueOf())
					? format(new Date(rowData.dateOfConviction as unknown as string), DISPLAY_DATE)
					: '',
		},
	] as IColumn<MotoringConviction>[];

	const getFormDate = (value?: string) => {
		if (!value) {
			return { year: '', month: '', day: '' };
		}
		const tempDate = new Date(value);
		const year = tempDate.getFullYear();
		const month = tempDate.getMonth();
		const day = tempDate.getDate();
		function minTwoDigits(n: number) {
			return (n < 10 ? '0' : '') + n.toString();
		}
		return {
			year: year.toString(),
			month: minTwoDigits(month + 1),
			day: minTwoDigits(day),
		};
	};

	const onEdit = (item: MotoringConviction, index: number) => {
		formik.setFormikState((state) => ({
			...state,
			values: {
				dateOfConviction: getFormDate(item.dateOfConviction),
				dateOfOffence: getFormDate(item.dateOfOffence),
				offenceCode: item.offenceCode,
				wasDisqualified: item.wasDisqualified ? 'yes' : 'no',
				banStartDate: getFormDate(item.banStartDate),
				banEndDate: getFormDate(item.banEndDate),
				numberOfPoints: item.numberOfPoints?.toString() as string,
				fineAmount: item.fineAmount?.toString() as string,
				isPending: item.isPending ? 'yes' : 'no',
			},
		}));
		setSelectedItemIndex(index);
		setShowForm(true);
	};

	const onRemove = (_item: MotoringConviction, index: number) => {
		setSelectedItemIndex(index);
		setIsOpenModal(true);
	};

	const onDelete = () => {
		if (selectedItemIndex === undefined) {
			setIsOpenModal(false);
			return;
		}
		const newArray = [...motoringConvictions];
		newArray.splice(selectedItemIndex, 1);
		if (newArray.length === 0) {
			formik.resetForm();
			setShowForm(true);
		}
		setMotoringConvictions(newArray);
		onChange(newArray);
		setIsOpenModal(false);
		setSelectedItemIndex(undefined);
	};

	return (
		<>
			<Box
				sx={(theme) => ({
					pr: '20%',
					flexDirection: 'column',
					justifyContent: 'space-around',
					[theme.breakpoints.down('md')]: {
						pr: 'unset',
					},
				})}
			>
				{motoringConvictions.length > 0 && (
					<Box>
						<Grid container direction='row' spacing={1}>
							<Grid
								size={{ xs: 12, md: 6 }}
								sx={(theme) => ({
									textAlign: 'right',
									[theme.breakpoints.down('md')]: { textAlign: 'unset' },
								})}
							>
								<FormLabel>Motoring Convictions</FormLabel>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<MiniTable
									data={motoringConvictions}
									columns={COLUMNS}
									onRemove={onRemove}
									onEdit={onEdit}
									isEditing={showForm}
									tableType={TableType.CONVICTIONS}
								/>
								<Collapse in={!showForm}>
									<Box display='flex' justifyContent='flex-end'>
										<Button
											sx={(theme) => ({
												my: 2,
												[theme.breakpoints.down('md')]: {
													width: '100%',
												},
											})}
											variant='contained'
											color='primary'
											onClick={() => setShowForm(true)}
										>
											Add Another Offence
										</Button>
									</Box>
								</Collapse>
							</Grid>
						</Grid>
					</Box>
				)}
			</Box>
			<Collapse in={showForm}>
				{showForm && (
					<Box
						sx={(theme) => ({
							backgroundColor: theme.palette.grey[300],
							borderRadius: 2,
							my: 2,
							p: 2,
							pt: 3,
							'& .MuiOutlinedInput-root': {
								backgroundColor: theme.palette.common.white,
							},
						})}
					>
						<DatePickerField
							id='dateOfConviction'
							minYear={maxConvictionDate}
							onChange={(value) => formik.setFieldValue('dateOfConviction', value)}
							label='Date of conviction'
							value={formik.values.dateOfConviction}
							error={
								(formik.touched.dateOfConviction?.day && Boolean(formik.errors.dateOfConviction?.day)) ||
								(formik.touched.dateOfConviction?.month && Boolean(formik.errors.dateOfConviction?.month)) ||
								(formik.touched.dateOfConviction?.year && Boolean(formik.errors.dateOfConviction?.year))
							}
							helperText={
								(formik.touched.dateOfConviction?.day && formik.errors.dateOfConviction?.day) ||
								(formik.touched.dateOfConviction?.month && formik.errors.dateOfConviction?.month) ||
								(formik.touched.dateOfConviction?.year && formik.errors.dateOfConviction?.year)
							}
						/>

						<AutoCompleteFormField
							label='What is their conviction code?'
							name='offenceCode'
							id='offenceCode'
							value={formik.values.offenceCode}
							onChange={(value) => formik.setFieldValue('offenceCode', value)}
							options={lists.convictionCodes}
							error={formik.touched.offenceCode && Boolean(formik.errors.offenceCode)}
							helperText={formik.touched.offenceCode && formik.errors.offenceCode}
						/>
						<RadioGroupButton
							label='Disqualified'
							name='wasDisqualified'
							id='wasDisqualified'
							value={formik.values.wasDisqualified}
							onChange={(value) => formik.setFieldValue('wasDisqualified', value)}
							error={formik.touched.wasDisqualified && Boolean(formik.errors.wasDisqualified)}
							helperText={formik.touched.wasDisqualified && formik.errors.wasDisqualified}
						/>
						<Collapse in={formik.values.wasDisqualified === 'yes'}>
							{formik.values.wasDisqualified === 'yes' && (
								<>
									<DatePickerField
										id='banStartDate'
										onChange={(value) => formik.setFieldValue('banStartDate', value)}
										label='Ban start date'
										value={formik.values.banStartDate}
										error={
											(formik.touched?.banStartDate?.day && Boolean(formik.errors.banStartDate?.day)) ||
											(formik.touched.banStartDate?.month && Boolean(formik.errors.banStartDate?.month)) ||
											(formik.touched.banStartDate?.year && Boolean(formik.errors.banStartDate?.year))
										}
										helperText={
											(formik.touched.banStartDate?.day && formik.errors.banStartDate?.day) ||
											(formik.touched.banStartDate?.month && formik.errors.banStartDate?.month) ||
											(formik.touched.banStartDate?.year && formik.errors.banStartDate?.year)
										}
									/>
									<DatePickerField
										id='banEndDate'
										onChange={(value) => formik.setFieldValue('banEndDate', value)}
										label='Ban end date'
										value={formik.values.banEndDate}
										error={
											(formik.touched?.banEndDate?.day && Boolean(formik.errors.banEndDate?.day)) ||
											(formik.touched.banEndDate?.month && Boolean(formik.errors.banEndDate?.month)) ||
											(formik.touched.banEndDate?.year && Boolean(formik.errors.banEndDate?.year))
										}
										helperText={
											(formik.touched.banEndDate?.day && formik.errors.banEndDate?.day) ||
											(formik.touched.banEndDate?.month && formik.errors.banEndDate?.month) ||
											(formik.touched.banEndDate?.year && formik.errors.banEndDate?.year)
										}
									/>
								</>
							)}
						</Collapse>
						<SelectFormField
							label='Number of penalty points'
							name='numberOfPoints'
							id='numberOfPoints'
							value={formik.values.numberOfPoints}
							onChange={(value) => formik.setFieldValue('numberOfPoints', value)}
							options={PENALTY_POINTS as IOptions[]}
							error={formik.touched.numberOfPoints && Boolean(formik.errors.numberOfPoints)}
							helperText={formik.touched.numberOfPoints && formik.errors.numberOfPoints}
						/>
						<FormTextField
							id='fineAmount'
							name='fineAmount'
							label='Fine Amount (£)'
							error={formik.touched.fineAmount && Boolean(formik.errors.fineAmount)}
							helperText={formik.touched.fineAmount && formik.errors.fineAmount}
							onChange={formik.handleChange}
							value={formik.values.fineAmount}
							format='currency'
						/>
						<RadioGroupButton
							label='Pending'
							name='isPending'
							id='isPending'
							value={formik.values.isPending}
							onChange={(value) => formik.setFieldValue('isPending', value)}
							error={formik.touched.isPending && Boolean(formik.errors.isPending)}
							helperText={formik.touched.isPending && formik.errors.isPending}
						/>
						<Box
							display='flex'
							justifyContent='flex-end'
							sx={(theme) => ({
								pr: '20%',
								[theme.breakpoints.down('md')]: {
									pr: 'unset',
								},
							})}
						>
							<Button
								sx={(theme) => ({
									my: 2,
									mr: 2,
									minWidth: '10%',
									[theme.breakpoints.down('md')]: {
										width: '50%',
									},
								})}
								variant='contained'
								color='secondary'
								disabled={motoringConvictions.length === 0}
								onClick={() => {
									setShowForm(false);
									formik.resetForm();
									setSelectedItemIndex(undefined);
								}}
							>
								Cancel
							</Button>
							<Button
								sx={(theme) => ({
									my: 2,
									minWidth: '100px',
									[theme.breakpoints.down('md')]: {
										width: '50%',
									},
								})}
								variant='contained'
								color='primary'
								onClick={() => formik.handleSubmit()}
							>
								{selectedItemIndex !== undefined && Number(selectedItemIndex) >= 0 ? 'Edit' : 'Add'}
							</Button>
						</Box>
					</Box>
				)}
			</Collapse>
			<HelperModal
				open={isOpenModal}
				onClose={() => {
					setSelectedItemIndex(undefined);
					setIsOpenModal(false);
				}}
				title='Remove Motoring Conviction'
				description='Are you sure you want to remove the motoring conviction?'
				closeText='Cancel'
				submitText='Delete'
				onSubmit={() => onDelete()}
			/>
		</>
	);
};

export default MotoringConvictionsForm;
